import { roundMoney } from '../../utils/money';
import type { TransportDirection } from './transportDirection';

/**
 * The whole truck in PLN as the sum of its saved shares — the one place this
 * sum is made for the truck card (freight cards' share of the truck, the
 * freight key bar, the „Koszt” tile and its „≈ … zł” line); backend mirror
 * `utils/truckSharesPln.js` gives the panel's rest of the truck. Nothing is
 * converted here: the backend saves every participant's share in PLN at the
 * FX snapshot of the truck (ZD `freightShare.amountPLN`, ZK
 * `allocatedTransportCost`), and those shares add up to the whole rate — on an
 * INBOUND truck the ZD carries all of it and the ZK keep 0, on an OUTBOUND
 * truck the ZK carry all of it (its ZD keep none, WP14), on a legacy mixed
 * truck both take their part.
 *
 * `null` (show nothing / „—”) when a ZD that carries a share has none readable
 * (the sum would be a lower bound) or when nothing is saved yet. Without
 * transport-cost access the shares are redacted, so this never yields an
 * amount the caller could not already read.
 */
export interface TruckSharesInput {
  direction: TransportDirection | null | undefined;
  supplierOrderCount: number;
  /** Sum of the ZD shares; `null` when any ZD has no readable share. */
  supplierTotalPLN: number | null;
  /** The ZK shares in PLN (positive ones; none on an INBOUND truck). */
  customerSharesPLN: number[];
}

export function truckSharesTotalPLN(input: TruckSharesInput): number | null {
  const supplierShares = input.direction !== 'OUTBOUND' && input.supplierOrderCount > 0;
  if (supplierShares && input.supplierTotalPLN === null) return null;
  const total = (supplierShares ? input.supplierTotalPLN ?? 0 : 0)
    + input.customerSharesPLN.reduce((sum, value) => sum + (Number(value) || 0), 0);
  return total > 0 ? roundMoney(total) : null;
}
